import * as React from 'react';
import {
  gridColumnDefinitionsSelector,
  useGridSelector,
  useGridApiContext,
  GridPanelContent,
  GridPanelFooter,
  GridPanelHeader,
  GridPanelWrapper,
  gridColumnVisibilityModelSelector,
} from '@mui/x-data-grid-pro';
import { Typography, FormControlLabel, Button, Switch, Box } from '@mui/material';

export interface DataGridColumnsPanelProps {
  suppressFields?: string[];
  showHideAll?: boolean;
  onResetColumnState?: () => void;
}

export function DataGridColumnsPanel({
  suppressFields = [],
  showHideAll,
  onResetColumnState,
}: DataGridColumnsPanelProps) {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  const currentColumns = React.useMemo(
    // always suppress the checkbox selection column from columns panel
    () => columns.filter((column) => !suppressFields.concat(['__check__']).includes(column.field)),
    [columns, suppressFields]
  );

  const toggleColumn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: field } = event.target as HTMLInputElement;
    apiRef.current.setColumnVisibility(field, columnVisibilityModel[field] === false);
  };

  const toggleAllColumns = React.useCallback(
    (isVisible: boolean) => {
      const currentModel = gridColumnVisibilityModelSelector(apiRef);
      const newModel = { ...currentModel };

      currentColumns.forEach((col) => {
        if (col.hideable) {
          if (isVisible) {
            // delete the key from the model instead of setting it to `true`
            delete newModel[col.field];
          } else {
            newModel[col.field] = false;
          }
        }
      });

      return apiRef.current.setColumnVisibilityModel(newModel);
    },
    [apiRef, currentColumns]
  );

  const hideAllColumns = () => {
    toggleAllColumns(false);
  };

  const showAllColumns = () => {
    toggleAllColumns(true);
  };

  return (
    <GridPanelWrapper style={{ width: 300, maxHeight: 450 }}>
      <GridPanelHeader>
        <Typography paddingX={1} variant='h5'>
          Show/Hide Columns
        </Typography>
      </GridPanelHeader>
      <GridPanelContent>
        <Box padding={1}>
          {currentColumns.map((column) => (
            <Box
              key={column.field}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1px 8px 1px 7px',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    sx={{ mr: 0.5 }}
                    checked={
                      apiRef.current.state.columns.columnVisibilityModel[column.field] === undefined
                        ? true
                        : apiRef.current.state.columns.columnVisibilityModel[column.field]
                    }
                    onChange={toggleColumn}
                    name={column.field}
                    color='primary'
                    size='small'
                  />
                }
                label={column.headerName || column.field}
                labelPlacement='start'
                sx={{
                  width: `100%`,
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    flexGrow: 1,
                  },
                }}
              />
              {/* TODO: make fields draggable to reorder */}
              {/* {!rootProps.disableColumnReorder && (
                <IconButton
                  sx={{ justifyContent: 'flex-end' }}
                  draggable
                  aria-label={apiRef.current.getLocaleText('columnsPanelDragIconLabel')}
                  title={apiRef.current.getLocaleText('columnsPanelDragIconLabel')}
                  size='small'
                  disabled
                >
                  <GridDragIcon />
                </IconButton>
              )} */}
            </Box>
          ))}
        </Box>
      </GridPanelContent>
      <GridPanelFooter>
        <div>
          {showHideAll && (
            <Button onClick={hideAllColumns} color='primary'>
              {apiRef.current.getLocaleText('columnsPanelHideAllButton')}
            </Button>
          )}
          <Button onClick={showAllColumns} color='primary'>
            {apiRef.current.getLocaleText('columnsPanelShowAllButton')}
          </Button>
        </div>
        {onResetColumnState && (
          <Button color='primary' onClick={onResetColumnState}>
            Reset
          </Button>
        )}
      </GridPanelFooter>
    </GridPanelWrapper>
  );
}
